import { useState } from "react";
import { Link } from "react-router-dom";
import mascot from "@/assets/bailbuddies-mascot.png";
import usePageMeta from "@/hooks/usePageMeta";

const tickerPhrases = [
  "PLANS WITHOUT PRESSURE",
  "YOUR PEOPLE · ONE TAP",
  "SECRET BAIL STAYS PRIVATE",
  "RIGHT NOW · TODAY · TONIGHT",
  "CHANGE YOUR MIND ANYTIME",
  "GROUP CHAT ENERGY",
];

const steps = [
  {
    n: "01",
    title: "Start a plan",
    body: "Set a time, a place and the people. Share a link — friends can RSVP in the browser before they install anything.",
  },
  {
    n: "02",
    title: "Check the vibe",
    body: "RSVPs can change. Secret Bail stays private and never withdraws you from the plan.",
  },
  {
    n: "03",
    title: "Let the plan find its shape",
    body: "People can go, maybe, or privately hope it disappears. If everyone secretly agrees, the plan can dissolve.",
  },
  {
    n: "04",
    title: "Keep it human",
    body: "No public reliability leaderboard. No guilt. Just your people, the plan, and room for life to happen.",
  },
];

const peopleRows = [
  { initials: "MK", name: "Marcus", note: "Pizza · 3 recent plans" },
  { initials: "JR", name: "Jordan", note: "Walks · 2 recent plans" },
  { initials: "TS", name: "Tasha", note: "Karaoke · usual crew" },
];

const testimonials = [
  {
    quote: "“We booked a table for six and dissolved it in eleven minutes. Best night we never had.”",
    name: "Demo chat",
    fill: "bg-sticker-yellow",
  },
  {
    quote: "“I used to write three-paragraph excuses. Now I tap out and go to bed without a thread.”",
    name: "Demo chat",
    fill: "bg-sticker-mint",
  },
  {
    quote: "“Two of us still wanted karaoke. The other four folded. We went as a duo. Fine by us.”",
    name: "Demo chat",
    fill: "bg-sticker-pink",
  },
];

const avatars = [
  { fill: "bg-sticker-yellow", initials: "MK" },
  { fill: "bg-sticker-mint", initials: "JR" },
  { fill: "bg-sticker-pink", initials: "TS" },
  { fill: "bg-sticker-surface", initials: "+9" },
];

const GhostMark = ({ size = 26, className = "" }: { size?: number; className?: string }) => (
  <img
    src={mascot}
    alt=""
    aria-hidden="true"
    width={size}
    height={size}
    style={{ width: size, height: size }}
    className={`rounded-full border-2 border-sticker-ink object-cover ${className}`}
  />
);

type RosterEntry = { name: string; excuse: string; status: "OUT" | "IN" | "?"; fill: string };

const baseRoster: RosterEntry[] = [
  { name: "Marcus", excuse: "“my dog is being weird”", status: "OUT", fill: "bg-sticker-yellow" },
  { name: "Jordan", excuse: "“work thing, sorry sorry”", status: "OUT", fill: "bg-sticker-mint" },
  { name: "Tasha", excuse: "already picked an outfit", status: "IN", fill: "bg-sticker-pink" },
  { name: "Ellis", excuse: "hasn’t opened the app", status: "?", fill: "bg-sticker-surface" },
];

const statusStyles: Record<RosterEntry["status"], string> = {
  OUT: "bg-sticker-pink text-sticker-on-pink",
  IN: "bg-sticker-mint text-sticker-ink",
  "?": "bg-sticker-surface text-sticker-ink",
};

const PhoneMock = () => {
  const [bailed, setBailed] = useState(false);

  const roster = baseRoster;

  return (
    <div
      className="w-[334px] shrink-0 rounded-[38px] border-4 border-sticker-ink bg-sticker-surface shadow-sticker-12"
      style={{ padding: "22px 20px 26px" }}
    >
      <div className="flex flex-col gap-[13px]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="sticker-label text-sticker-purple">TONIGHT · 8:30 PM</p>
            <h3 className="font-sticker-display text-[24px] leading-[1.05] text-sticker-ink">
              Karaoke, allegedly
            </h3>
          </div>
          <div className="rounded-[14px] border-3 border-sticker-ink bg-sticker-yellow px-3 py-1.5 text-center shadow-sticker-3">
            <div className="font-sticker-display text-[20px] leading-none text-sticker-ink">
              14
            </div>
            <div className="sticker-label text-[9px] text-sticker-ink">STREAK</div>
          </div>
        </div>

        <div className="rounded-[16px] border-3 border-sticker-ink bg-sticker-yellow p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="sticker-label text-sticker-ink">BAIL METER</span>
            <span className="sticker-label text-sticker-ink">PRIVATE</span>
          </div>
          <div className="h-[15px] overflow-hidden rounded-full border-2 border-sticker-ink bg-sticker-surface">
            <div
              className="h-full rounded-full border-r-2 border-sticker-ink bg-sticker-purple"
              style={{
                width: bailed ? "50%" : "33%",
                transition: "width 420ms cubic-bezier(.2,.8,.2,1)",
              }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between font-sticker-body text-[11px] text-sticker-ink">
            <span style={{ fontWeight: 700 }}>{bailed ? "Secret bail saved" : "Private group signal"}</span>
            <span style={{ fontWeight: 700 }}>RSVP unchanged</span>
          </div>
        </div>

        {roster.map((r) => (
          <div
            key={r.name}
            className="flex items-center gap-2.5 rounded-[16px] border-3 border-sticker-ink bg-sticker-surface px-2.5 py-2"
          >
            <div
              className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border-2 border-sticker-ink ${r.fill}`}
            >
              <span className="font-sticker-body text-[11px] text-sticker-ink" style={{ fontWeight: 800 }}>
                {r.name.slice(0, 1)}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-sticker-body text-[14px] text-sticker-ink" style={{ fontWeight: 800 }}>
                {r.name}
              </div>
              <div
                className="truncate font-sticker-body text-[11px] text-sticker-body"
                style={{ fontWeight: 500 }}
              >
                {r.excuse}
              </div>
            </div>
            <span
              className={`shrink-0 rounded-full border-2 border-sticker-ink px-2 py-0.5 sticker-label text-[10px] ${statusStyles[r.status]}`}
            >
              {r.status}
            </span>
          </div>
        ))}

        <button
          type="button"
          onClick={() => setBailed((b) => !b)}
          aria-pressed={bailed}
          className={`sticker-press w-full rounded-[20px] border-3 border-sticker-ink py-3 font-sticker-display text-[18px] shadow-sticker-5 ${
            bailed ? "bg-sticker-mint text-sticker-ink" : "bg-sticker-pink text-sticker-on-pink"
          }`}
        >
          {bailed ? "SECRET BAIL SAVED" : "SECRET BAIL"}
        </button>
      </div>
    </div>
  );
};

const Index = () => {
  usePageMeta(
    "BailBuddies — Plans without the pressure.",
    "Make plans with your people without turning changing your mind into a moral failure. Secret Bail stays private and never changes your RSVP.",
  );

  return (
    <div className="min-h-screen bg-sticker-page font-sticker-body text-sticker-ink">
      <header className="mx-auto flex max-w-[1280px] flex-wrap items-center justify-between gap-4 px-6 py-6 md:px-10">
        <Link to="/" className="flex items-center gap-3">
          <img
            src={mascot}
            alt="BailBuddies ghost mascot"
            width={64}
            height={64}
            className="h-16 w-16 rounded-[20px] border-4 border-sticker-ink object-cover shadow-sticker-5"
            fetchPriority="high"
          />
          <span className="font-sticker-display text-[32px] tracking-[-0.04em]">
            <span className="text-sticker-purple">Bail</span>
            <span className="text-sticker-ink">Buddies</span>
          </span>
        </Link>

        <nav className="flex items-center gap-6">
          <a href="#how" className="hidden text-[15px] text-sticker-ink sm:inline" style={{ fontWeight: 700 }}>
            How it works
          </a>
          <a href="#streaks" className="hidden text-[15px] text-sticker-ink sm:inline" style={{ fontWeight: 700 }}>
            Streaks
          </a>
          <a href="#share" className="hidden text-[15px] text-sticker-ink sm:inline" style={{ fontWeight: 700 }}>
            Bail Wrapped
          </a>
          <Link
            to="/auth"
            className="sticker-press rounded-full border-3 border-sticker-ink bg-sticker-purple px-[22px] py-3 text-[15px] text-sticker-on-purple shadow-sticker-4"
            style={{ fontWeight: 700 }}
          >
            Sign In
          </Link>
        </nav>
      </header>

      <section className="mx-auto max-w-[1280px] px-6 pb-16 pt-6 md:px-10">
        <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,1.02fr)_minmax(0,0.98fr)]">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <span
                className="rounded-full border-3 border-sticker-ink bg-sticker-yellow px-4 py-2 text-[13px] text-sticker-ink shadow-sticker-3"
                style={{ fontWeight: 800, transform: "rotate(-1.5deg)" }}
              >
                Bail meter · private
              </span>
              <span
                className="flex items-center gap-2 rounded-full border-3 border-sticker-ink bg-sticker-mint px-4 py-2 text-[14px] text-sticker-ink shadow-sticker-3"
                style={{ fontWeight: 800, transform: "rotate(2deg)" }}
              >
                <GhostMark size={26} />
                Cancel culture, but in a fun way
              </span>
            </div>

            <h1 className="mt-7 font-sticker-display text-[48px] uppercase leading-[0.9] tracking-[-0.04em] md:text-[72px]">
              Making plans is weird.
              <br />
              BailBuddies gets
              <br />
              <span className="text-sticker-purple">the group chat.</span>
            </h1>

            <p
              className="mt-6 max-w-[500px] text-[20px] leading-[1.5] text-sticker-body"
              style={{ fontWeight: 500 }}
            >
              Make the plan. Feel excited, anxious, unsure, or all three. BailBuddies gives the group room to change its mind without the guilt trip.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/auth?next=/create"
                className="sticker-press rounded-full border-3 border-sticker-ink bg-sticker-pink px-[30px] py-[17px] font-sticker-display text-[18px] text-sticker-on-pink shadow-sticker-6"
              >
                Start a plan →
              </Link>
              <a
                href="#share"
                className="sticker-press rounded-full border-3 border-sticker-ink bg-sticker-surface px-[30px] py-[17px] text-[17px] text-sticker-ink shadow-sticker-6"
                style={{ fontWeight: 800 }}
              >
                See a Bail Wrapped
              </a>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="flex items-center">
                {avatars.map((a, i) => (
                  <div
                    key={a.initials}
                    className={`flex h-9 w-9 items-center justify-center rounded-full border-3 border-sticker-ink ${a.fill}`}
                    style={{ marginLeft: i === 0 ? 0 : -10 }}
                  >
                    <span className="text-[11px] text-sticker-ink" style={{ fontWeight: 800 }}>
                      {a.initials}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-[14px] text-sticker-body" style={{ fontWeight: 600 }}>
                Friends join from a link. They can RSVP before they install.
              </p>
            </div>
          </div>

          <div className="pb-6 pl-3.5 pr-6 pt-3.5">
            <div className="relative w-[min(420px,100%)] animate-sticker-float rounded-[40px] border-5 border-sticker-ink bg-sticker-surface p-[26px] shadow-sticker-14">
              <img
                src={mascot}
                alt="BailBuddies ghost lounging on a couch"
                className="h-[352px] w-full rounded-[26px] border-4 border-sticker-ink object-cover"
              />
              <h2 className="mt-5 text-center font-sticker-display text-[34px] uppercase leading-[0.95]">
                Patron saint of
                <br />
                <span className="text-sticker-purple">staying in</span>
              </h2>
              <p
                className="mt-3 text-center text-[15px] text-sticker-body"
                style={{ fontWeight: 600 }}
              >
                Plans can happen, shrink, change, or disappear. Nobody needs a scoreboard.
              </p>

              <span
                className="absolute -right-[18px] -top-[22px] rounded-full border-4 border-sticker-ink bg-sticker-yellow px-4 py-2 font-sticker-display text-[15px] text-sticker-ink"
                style={{ transform: "rotate(6deg)" }}
              >
                6/6 TONIGHT
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="overflow-hidden border-y-4 border-sticker-ink bg-sticker-purple py-3.5">
        <div className="flex w-max animate-sticker-ticker gap-10">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex shrink-0 gap-10" aria-hidden={dup === 1}>
              {tickerPhrases.map((p) => (
                <span key={p} className="whitespace-nowrap font-sticker-display text-[16px] text-sticker-on-purple">
                  {p}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <section id="streaks" className="mx-auto max-w-[1280px] px-6 py-20 md:px-10">
        <h2 className="max-w-[820px] font-sticker-display text-[34px] uppercase leading-[0.95] md:text-[48px]">
          Built for how plans actually feel
        </h2>
        <p className="mt-4 text-[18px] text-sticker-body" style={{ fontWeight: 600 }}>
          Low pressure, private when it matters, and still fun.
        </p>

        <div className="mt-10 grid gap-[22px] md:grid-cols-3">
          <div className="rounded-[28px] border-4 border-sticker-ink bg-sticker-yellow p-[26px] shadow-sticker-8">
            <p className="sticker-label text-sticker-ink">Bail Streak</p>
            <div className="mt-4 font-sticker-display text-[58px] leading-[0.82] text-sticker-ink">14</div>
            <p className="mt-2 text-[13px] uppercase tracking-[0.1em] text-sticker-ink" style={{ fontWeight: 800 }}>
              Day streak
            </p>
            <p className="mt-4 text-[15px] leading-[1.5] text-sticker-secondary" style={{ fontWeight: 500 }}>
              A streak is just nights your group used the app together — staying in or going out.
              Shields are optional, never required because someone showed up.
            </p>
          </div>

          <div className="rounded-[28px] border-4 border-sticker-ink bg-sticker-surface p-[26px] shadow-sticker-8">
            <p className="sticker-label text-sticker-ink">Your People</p>
            <div className="mt-4 flex flex-col gap-2.5">
              {peopleRows.map((person) => (
                <div
                  key={person.name}
                  className="flex items-center gap-3 rounded-[12px] border-2 border-sticker-ink bg-sticker-page px-3 py-2"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-sticker-ink bg-sticker-yellow text-[11px] font-extrabold">
                    {person.initials}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] font-bold text-sticker-ink">{person.name}</span>
                    <span className="block truncate text-[11px] font-semibold text-sticker-body">{person.note}</span>
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[15px] leading-[1.5] text-sticker-secondary" style={{ fontWeight: 500 }}>
              The people you actually make plans with rise to the top, so inviting the usual crew takes one tap.
            </p>
          </div>

          <div className="rounded-[28px] border-4 border-sticker-ink bg-sticker-mint p-[26px] shadow-sticker-8">
            <p className="sticker-label text-sticker-ink">Social bandwidth</p>
            <div className="mt-5 flex h-[66px] w-[66px] items-center justify-center rounded-full border-4 border-dashed border-sticker-ink bg-sticker-surface">
              <span className="font-sticker-display text-[24px] text-sticker-ink">?</span>
            </div>
            <p className="mt-5 text-[15px] leading-[1.5] text-sticker-secondary" style={{ fontWeight: 500 }}>
              In, maybe, or privately hoping the plan disappears. Your current feeling is information, not a character grade.
            </p>
          </div>
        </div>
      </section>

      <section id="how" className="mx-auto max-w-[1280px] px-6 pb-20 md:px-10">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <div className="flex justify-center lg:justify-start">
            <PhoneMock />
          </div>

          <div>
            <h2 className="font-sticker-display text-[34px] uppercase leading-[0.95] md:text-[48px]">
              How it works
            </h2>
            <div className="mt-9 flex flex-col gap-8">
              {steps.map((s) => (
                <div key={s.n} className="flex gap-5">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] border-3 border-sticker-ink bg-sticker-purple font-sticker-display text-[17px] text-sticker-on-purple shadow-sticker-3">
                    {s.n}
                  </div>
                  <div>
                    <h3 className="font-sticker-display text-[23px] leading-[1.05]">{s.title}</h3>
                    <p className="mt-2 text-[16px] leading-[1.5] text-sticker-secondary" style={{ fontWeight: 500 }}>
                      {s.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="share" className="border-y-4 border-sticker-ink bg-sticker-purple py-[84px]">
        <div className="mx-auto grid max-w-[1280px] items-center gap-14 px-6 md:px-10 lg:grid-cols-2">
          <div>
            <span
              className="inline-block rounded-full border-3 border-sticker-ink bg-sticker-yellow px-4 py-2 sticker-label text-sticker-ink shadow-sticker-3"
              style={{ transform: "rotate(-3deg)" }}
            >
              Built for 9:16
            </span>

            <h2 className="mt-6 font-sticker-display text-[36px] uppercase leading-[0.92] text-sticker-on-purple md:text-[50px]">
              Your Bail Wrapped posts itself.
            </h2>

            <p className="mt-5 max-w-[500px] text-[18px] leading-[1.5] text-sticker-on-purple-2" style={{ fontWeight: 500 }}>
              Every dissolved plan quietly feeds a recap your group will actually want to share.
            </p>

            <ul className="mt-6 flex flex-col gap-3 text-[17px] text-sticker-on-purple-2" style={{ fontWeight: 500 }}>
              <li>— An auto-generated recap of every plan your crew folded this month.</li>
              <li>— One-tap sharing straight to Stories, sized 9:16 and ready to go.</li>
              <li>— Group stats: fastest bail, top excuse, and hours handed back to the couch.</li>
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <span className="sticker-press rounded-full border-3 border-sticker-ink bg-sticker-surface px-5 py-3 text-[15px] text-sticker-ink shadow-sticker-4" style={{ fontWeight: 800 }}>
                Copy link
              </span>
              <span className="sticker-press rounded-full border-3 border-sticker-ink bg-sticker-mint px-5 py-3 text-[15px] text-sticker-ink shadow-sticker-4" style={{ fontWeight: 800 }}>
                Save video
              </span>
              <span className="sticker-press rounded-full border-3 border-sticker-ink bg-sticker-pink px-5 py-3 text-[15px] text-sticker-on-pink shadow-sticker-4" style={{ fontWeight: 800 }}>
                Share to Stories
              </span>
            </div>
          </div>

          <div className="flex justify-center lg:justify-end">
            <div className="w-[min(340px,100%)] rounded-[32px] border-5 border-sticker-ink bg-sticker-yellow p-6 shadow-sticker-14" style={{ aspectRatio: "9 / 16" }}>
              <div className="flex h-full flex-col">
                <div className="flex items-center gap-3">
                  <GhostMark size={34} />
                  <div>
                    <div className="sticker-label text-sticker-ink">Bail Wrapped</div>
                    <div className="text-[12px] text-sticker-body" style={{ fontWeight: 700 }}>
                      @bailbuddies
                    </div>
                  </div>
                </div>

                <div className="mt-6 font-sticker-display text-[80px] leading-[0.82] text-sticker-purple">47</div>
                <p className="mt-3 font-sticker-display text-[19px] uppercase leading-[1.05] text-sticker-ink">
                  Plans dissolved with the same six people
                </p>

                <div className="my-5 h-[3px] w-full bg-sticker-ink" />

                <div className="flex flex-col gap-2.5">
                  {[
                    ["Fastest bail", "4 min"],
                    ["Rank", "Top 3%"],
                    ["Reclaimed", "61 hrs"],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-2.5 whitespace-nowrap">
                      <span className="text-[14px] text-sticker-body" style={{ fontWeight: 700 }}>{k}</span>
                      <span className="font-sticker-display text-[16px] text-sticker-ink">{v}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-auto">
                  <span className="inline-block rounded-full border-2 border-sticker-ink bg-sticker-surface px-3 py-1.5 text-[12px] text-sticker-ink" style={{ fontWeight: 700 }}>
                    Top excuse: “my dog is being weird” ×9
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-6 py-20 md:px-10">
        <h2 className="font-sticker-display text-[32px] uppercase leading-[0.95] md:text-[46px]">
          How a demo chat sounds
        </h2>

        <div className="mt-10 grid gap-[22px] md:grid-cols-3">
          {testimonials.map((t) => (
            <figure key={t.quote} className="rounded-[26px] border-4 border-sticker-ink bg-sticker-surface p-[26px] shadow-sticker-8">
              <blockquote className="text-[17px] leading-[1.5] text-sticker-secondary" style={{ fontWeight: 600 }}>
                {t.quote}
              </blockquote>
              <figcaption className="mt-5 flex items-center gap-3">
                <span className={`flex h-8 w-8 items-center justify-center rounded-full border-2 border-sticker-ink ${t.fill}`}>
                  <span className="text-[11px] text-sticker-ink" style={{ fontWeight: 800 }}>{t.name.slice(0, 1)}</span>
                </span>
                <span className="text-[13px] text-sticker-body" style={{ fontWeight: 700 }}>{t.name}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section id="get" className="mx-auto max-w-[1280px] px-6 pb-20 md:px-10">
        <div className="rounded-[36px] border-5 border-sticker-ink bg-sticker-pink p-8 shadow-sticker-14 md:p-14">
          <div className="flex flex-col items-start gap-8 md:flex-row md:items-center">
            <img
              src={mascot}
              alt="BailBuddies ghost mascot waving"
              width={190}
              height={198}
              className="h-[198px] w-[190px] shrink-0 rounded-[26px] border-5 border-sticker-ink object-cover"
              loading="lazy"
            />
            <div>
              <h2 className="font-sticker-display text-[34px] uppercase leading-[0.92] text-sticker-on-pink md:text-[52px]">
                The group already started without you.
              </h2>
              <p className="mt-4 text-[19px] text-sticker-on-pink" style={{ fontWeight: 600 }}>
                Free. Takes a minute. First streak starts tonight.
              </p>
              <Link
                to="/auth?next=/create"
                className="sticker-press mt-7 inline-block rounded-full border-3 border-sticker-ink bg-sticker-surface px-[30px] py-[17px] font-sticker-display text-[18px] text-sticker-ink shadow-sticker-6"
              >
                Start a plan
              </Link>
              <p className="mt-4 text-[14px] text-sticker-on-pink" style={{ fontWeight: 700 }}>
                Works in the browser. Add it to your home screen later if you want.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t-4 border-sticker-ink">
        <div className="mx-auto max-w-[1280px] px-6 py-8 md:px-10">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <GhostMark size={38} />
              <span className="font-sticker-display text-[17px] text-sticker-ink">BailBuddies</span>
            </div>
            <p className="text-[14px] text-sticker-body" style={{ fontWeight: 700 }}>
              No guilt. Just shared relief.
            </p>
          </div>

          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
            {[
              ["/faq", "FAQ"],
              ["/pricing", "Pricing"],
              ["/install", "Add to phone"],
              ["/terms", "Terms"],
              ["/privacy", "Privacy"],
            ].map(([href, label]) => (
              <Link key={href} to={href} className="text-[14px] text-sticker-body" style={{ fontWeight: 700 }}>
                {label}
              </Link>
            ))}
            <a href="mailto:hello@bailbuddies.app" className="text-[14px] text-sticker-body" style={{ fontWeight: 700 }}>
              hello@bailbuddies.app
            </a>
          </div>

          <p className="mt-5 text-[13px] text-sticker-body" style={{ fontWeight: 500 }}>
            © {new Date().getFullYear()} BailBuddies. Made for plans, people, and changing your mind.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
